import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import { showSideMenu } from "../../../../../App";
import channelsHelper from "../../../../API/Channels/channelsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";

const ChannelsList = ({ isFunctionsLoading, setDisplayedView }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace } = useWorkspaces();
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedChannelId, setSelectedChannelId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [displayedChannels, setDisplayedChannels] = React.useState([]);
  const [selectedChannelIds, setSelectedChannelIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/channels/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.25,
    },
    {
      title: t("settings.list.description"),
    },
    {
      title: t("settings.list.channel"),
      width: 0.25,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];

  const loadChannels = async (workspaceID) => {
    try {
      setIsLoading(true);
      const channelsData = await channelsHelper.getChannels(workspaceID, null);
      let channels =
        channelsData?.data?.CommunicationServicesByWorkspace?.items;
      channels =
        channels
          ?.map((u) => {
            if (u?._deleted !== true)
              return {
                ...u,
              };
          })
          ?.filter((u) => u != null) || [];
      dispatch("SET_CHANNELS", channels || []);

      setDisplayedChannels(channels);
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadChannels(state?.activeWorkspaceID);
  }, [state?.activeWorkspaceID]);
  useEffect(() => {
    setDisplayedChannels([...state?.channels]);
  }, [JSON.stringify([...state?.channels])]);

  function identifyOptByID(id) {
    return state.channels.find((u) => u.id === id);
  }
  const handleHostingItemSelection = ({ item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !item?.id) return;
    history.push(`/workspaces/${wsId}/channels/${item?.id}`);
  };
  const handleHostingItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedChannelId || selectedItem?.id;
      if (_id) {
        const functionsData = await channelsHelper.getChannels(
          state?.activeWorkspaceID,
          null
        );
        let functions =
          functionsData?.data?.CommunicationServicesByWorkspace?.items;
        functions =
          functions
            ?.map((u) => {
              if (u?.llmProvider === _id)
                return {
                  ...u,
                };
            })
            ?.filter((u) => u != null && u?._deleted != true) || [];
        if (functions.length > 0) {
          AppToasty.show(t("hostingDeletionErrorCaptionDueToConflict"), {
            type: "danger",
          });
          return;
        }
        const result = await channelsHelper.deleteChannel(_id);
        dispatch("SET_CHANNELS", [
          ...state?.channels.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("hostingDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedChannelId(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("authorisationDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      let skipped = 0;
      for (const id of selectedChannelIds) {
        const functionsData = await channelsHelper.getChannels(
          state?.activeWorkspaceID,
          null
        );
        const inUse = (
          functionsData?.data?.CommunicationServicesByWorkspace?.items || []
        )
          .filter((u) => u != null && u?._deleted != true)
          .some((u) => u?.llmProvider === id);
        if (inUse) {
          skipped += 1;
          continue;
        }
        await channelsHelper.deleteChannel(id);
      }
      dispatch("SET_CHANNELS", [
        ...state?.channels.filter((u) => !selectedChannelIds.includes(u.id)),
      ]);
      setSelectedChannelIds([]);
      setShowBulkDeleteChoice(false);
      if (skipped > 0) {
        AppToasty.show(t("hostingDeletionErrorCaptionDueToConflict"), {
          type: "danger",
        });
      } else {
        AppToasty.show(t("hostingDeletionSuccessCaption"), {
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("authorisationDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/channels/new`);
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteChannelConfirmation")}
        subCaption={t("settings.list.deleteChannelConfirmationMessage")}
        confirmCaption={t("settings.list.confirmDelete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleHostingItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);
  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteChannelConfirmation"),
        })}
        subCaption={t("settings.list.deleteChannelConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedChannelIds]);

  return (
    <View
      style={{
        paddingLeft: 10,
        flex: 1,
        width: showSideMenu() ? width - 220 : "100%",
        minWidth: "100%",
        overflow: isLoading && "hidden",
      }}
    >
      {getActiveWorkspace()?.subscriptionType === "base" ? (
        <AppPlaceholderView
          accessibilityLabel="channels-availability"
          wrapperStyle={{
            maxWidth: 400,
            height: 200,
            marginHorizontal: "auto",
            marginTop: 50,
          }}
          size={30}
          icon={AppAssets.icons.accessDenied}
          mainCaption={t("settings.list.channelsNotAvailable")}
          subCaption={t("settings.list.upgradeToEnableChannels")}
        />
      ) : (
        <>
          {isLoading ? (
            <ToothFairyLoadingPlaceholder
              backgroundColor={currentTheme?.theme?.softBackgroundColor}
              visible={true}
              containerStyle={{
                backgroundColor: currentTheme?.theme?.transparent,
                minHeight: 250,
              }}
              caption={t("settings.list.loadingChannels")}
            />
          ) : (
            <>
              {displayedChannels.length > 0 ? (
                <AppTable
                  isSelectable
                  showPagination
                  itemsPerPage={50}
                  hideSearch={false}
                  searchPlaceholder={t("settings.toolbar.searchChannels")}
                  onSelectionChange={(selectedItems) => {
                    setSelectedChannelIds(
                      selectedItems?.map((u) => u?.id) || []
                    );
                  }}
                  bulkActions={[
                    {
                      id: "DELETE",
                      label: t("settings.list.delete"),
                      icon: AppAssets.icons.delete,
                      isDestructive: true,
                    },
                  ]}
                  onBulkActionPress={(id) => {
                    if (id === "DELETE" && selectedChannelIds?.length > 0)
                      setShowBulkDeleteChoice(true);
                  }}
                  actionButtons
                  showDropDownMenuForActions
                  mainActionLabel={t("settings.list.edit")}
                  onClickMainAction={(item, index) => {
                    handleHostingItemSelection({
                      id: item?.id,
                      item,
                      index,
                    });
                  }}
                  onActionItemPress={(id, item, index) => {
                    if (id === "EDIT") {
                      handleHostingItemSelection({
                        id: item?.id,
                        item,
                        index,
                      });
                    } else if (id === "DELETE") {
                      setSelectedItem(item);
                      setSelectedIndex(index);
                      setSelectedChannelId(item?.id);
                      setShowChoice(true);
                    }
                  }}
                  actions={[
                    {
                      label: t("settings.list.edit"),
                      id: "EDIT",
                      icon: AppAssets.icons.edit,
                    },
                    {
                      label: t("settings.list.delete"),
                      id: "DELETE",
                      icon: AppAssets.icons.delete,
                    },
                  ]}
                  style={{
                    zIndex: -1,
                    marginTop: 15,
                    height:
                      displayedChannels?.length >= 50
                        ? height - 95
                        : height - 75,
                    paddingRight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  headers={columns}
                  items={displayedChannels
                    ?.sort((a, b) =>
                      (a?.name || "").localeCompare(b?.name || "")
                    )
                    ?.map((u) => {
                      return {
                        id: u?.id || " ",
                        name: u?.name || " ",
                        description:
                          u?.description ||
                          t("settings.list.noDescriptionProvided"),
                        channel: u?.channel?.toUpperCase() || " ",
                        actions: " ",
                      };
                    })}
                >
                  <ToothFairyButton
                    title={t("common.create")}
                    onPress={handleCreation}
                  />
                </AppTable>
              ) : (
                <AppPlaceholderView
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    marginVertical: 60,
                    marginHorizontal: "auto",
                    flex: 1,
                    zIndex: -1,
                  }}
                  size={30}
                  icon={AppAssets.icons.channels}
                  mainCaption={t("settings.list.noChannelsFound")}
                  subCaption={t("settings.list.noChannelsFoundSub")}
                  actionLabel={t("common.create")}
                  onActionPress={handleCreation}
                />
              )}
            </>
          )}
        </>
      )}

      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default ChannelsList;
